// ─── Activity helpers ─────────────────────────────────────────────────────────
//
// Pure filtering logic for the Activity Log: severity extraction, the
// "highlights only" rule, time-range windows, and the agent list used to
// populate the filter dropdown.

import type { ActivityEntry } from './api'

export type ActivitySeverity = 'success' | 'failed' | 'other'
export type SeverityFilter = 'ALL' | 'SUCCESS' | 'FAILED'

export function outcomeOf(entry: ActivityEntry): string {
  const o = entry.metadata?.outcome
  return typeof o === 'string' ? o : 'success'
}

export function severityOf(entry: ActivityEntry): ActivitySeverity {
  const o = outcomeOf(entry)
  if (o === 'failed') return 'failed'
  if (o === 'success') return 'success'
  return 'other'
}

export function triggerTypeOf(entry: ActivityEntry): string {
  const t = entry.metadata?.triggerType
  return typeof t === 'string' ? t : ''
}

/** Triggers that produce routine, low-signal noise when they succeed. */
const ROUTINE_TRIGGERS = new Set([
  'cron', 'schedule', 'scheduled', 'heartbeat', 'health', 'tick', 'poll',
])

/**
 * A "highlight" is anything worth Mark's attention: any non-success outcome, or
 * any event that was NOT a successful scheduled/heartbeat tick (manual runs,
 * webhooks, escalations, decisions). Successful routine ticks are hidden when
 * "highlights only" is on.
 */
export function isHighlight(entry: ActivityEntry): boolean {
  if (severityOf(entry) !== 'success') return true
  return !ROUTINE_TRIGGERS.has(triggerTypeOf(entry).toLowerCase())
}

export interface TimeRange {
  key: string
  label: string
  /** Window in ms; Infinity means "all time". */
  ms: number
}

export const ACTIVITY_RANGES: TimeRange[] = [
  { key: 'ALL', label: 'ALL', ms: Number.POSITIVE_INFINITY },
  { key: '1H', label: '1H', ms: 60 * 60 * 1000 },
  { key: '24H', label: '24H', ms: 24 * 60 * 60 * 1000 },
  { key: '7D', label: '7D', ms: 7 * 24 * 60 * 60 * 1000 },
]

export function getRange(key: string): TimeRange {
  return ACTIVITY_RANGES.find((r) => r.key === key) ?? ACTIVITY_RANGES[0]
}

export function withinRange(entry: ActivityEntry, ms: number, now = Date.now()): boolean {
  if (!Number.isFinite(ms)) return true
  return now - new Date(entry.createdAt).getTime() <= ms
}

export function distinctAgents(entries: ActivityEntry[]): string[] {
  return Array.from(new Set(entries.map((e) => e.agentName).filter(Boolean))).sort()
}

export interface ActivityFilterState {
  agent: string // agent name, or 'ALL'
  severity: SeverityFilter
  rangeKey: string
  highlightsOnly: boolean
}

/** Apply all active filters to the entry list. */
export function filterActivity(
  entries: ActivityEntry[],
  filter: ActivityFilterState,
  now = Date.now(),
): ActivityEntry[] {
  const range = getRange(filter.rangeKey)
  return entries.filter((e) => {
    if (filter.agent !== 'ALL' && e.agentName !== filter.agent) return false
    if (filter.severity === 'SUCCESS' && severityOf(e) !== 'success') return false
    if (filter.severity === 'FAILED' && severityOf(e) !== 'failed') return false
    if (!withinRange(e, range.ms, now)) return false
    if (filter.highlightsOnly && !isHighlight(e)) return false
    return true
  })
}
